import { z } from 'zod';
import { apiClient } from '../lib/api';
import { buildApiUrl } from '../utils/apiUrl';
import {
	ApiResponseSchema,
	PaginatedApiResponseSchema,
} from '../schemas';
import { UniversalChartDataSchema, NivoLineChartDataSchema } from '../schemas/chartSchema';

const NestedProvinceSchema = z.object({
	maTinh: z.string(),
	tenTinh: z.string(),
}).strip();

export const TruongTHPTSchema = z.object({
	maTruong: z.string(),
	// Vắng mặt cho trường chỉ biết nhóm sau sáp nhập (chưa biết tỉnh gốc) — xem
	// docs/dev-guide/truong-thpt-va-nhom-tinh-sap-nhap.md. Dùng `nhomSapNhap` để
	// hiện "trường thuộc nhóm/tỉnh nào" thay vì tự suy từ `maTinh`.
	maTinh: z.string().optional(),
	maTinhSapNhap: z.string().nullable().optional(),
	nhomSapNhap: z.string().optional(),
	maNoiBo: z.string().nullable(),
	tenTruong: z.string().transform(val => (val === "NULL" || val === "#Chưa xác định") ? "#Chưa xác định" : val),
	top149: z.boolean(),
	ghiChu: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
	tinhThanh: NestedProvinceSchema.optional(),
}).strip();

export const SchoolYearStatsSchema = z.object({
	maTruong: z.string(),
	maTinh: z.string().optional(),
	maNoiBo: z.string().nullable(),
	tenTruong: z.string(),
	top149: z.boolean(),
	ghiChu: z.string().nullable(),
	tenTinh: z.string().optional(),
	tenTinhSauSapNhap: z.string().optional(),
	maTruongSauSapNhap: z.string().optional(),
	hasCodeNameChange: z.boolean().optional(),
	yearData: z.record(z.string(), z.number())
}).strip();

export const SchoolStatsResponseSchema = z.object({
	years: z.array(z.number()),
	stats: z.array(SchoolYearStatsSchema),
	total: z.number()
}).strip();

export const SchoolOverviewStatsSchema = z.object({
	totalSchools: z.number(),
	schoolDetails: z.array(z.object({
		maTruong: z.string(),
		tenTruong: z.string(),
		maTinh: z.string().optional(),
		tenTinh: z.string().optional(),
		studentCount: z.number(),
		top149: z.boolean()
	}).strip())
}).strip();

export const SchoolStatsByIdResponseSchema = z.object({
	school: z.object({
		maTruong: z.string(),
		tenTruong: z.string(),
		maTinh: z.string().optional(),
		top149: z.boolean(),
		tinhThanh: z.object({
			maTinh: z.string(),
			tenTinh: z.string()
		}).strip().nullable()
	}).strip(),
	yearStats: z.array(z.object({
		year: z.number().nullable(),
		count: z.number()
	}).strip())
}).strip();

export const SchoolMajorGroupStatSchema = z.object({
	year: z.number().nullable(),
	maKhoa: z.string().nullable(),
	tenKhoa: z.string().nullable(),
	maNganh: z.string().nullable(),
	tenNganh: z.string().nullable(),
	count: z.number(),
}).strip();

export const SchoolAdmissionMethodStatSchema = z.object({
	year: z.number().nullable(),
	maPhuongThuc: z.string().nullable(),
	tenPhuongThuc: z.string().nullable(),
	count: z.number(),
}).strip();

export const SchoolNoteUpdateSchema = z.object({
	ghiChu: z.string().nullable(),
}).strip();

export const SchoolStatsByIdGroupedResponseSchema = SchoolStatsByIdResponseSchema.extend({
	majorGroupStats: z.array(SchoolMajorGroupStatSchema),
	admissionMethodStats: z.array(SchoolAdmissionMethodStatSchema),
}).strip();


export const SchoolQueryParamsSchema = z.object({
	query: z.string().optional(),
	page: z.number().optional(),
	limit: z.number().optional(),
	sortBy: z.string().optional(),
	sortOrder: z.enum(['asc', 'desc']).optional(),
	top149: z.boolean().optional(),
	admissionMethods: z.array(z.string()).optional(),
	years: z.array(z.number()).optional(),
	maKhoa: z.array(z.string()).optional(),
	maNganh: z.array(z.string()).optional(),
	maTinh: z.union([z.string(), z.array(z.string())]).optional(),
	maTinhMoi: z.array(z.string()).optional(),
	hasCodeNameChange: z.boolean().optional(),
}).strip();

export const SchoolHistorySchema = z.object({
	maTruong: z.string(),
	tenTruong: z.string(),
	currentSchool: z.object({
		maTruong: z.string(),
		tenTruong: z.string(),
		maTinh: z.string().optional(),
		nhomSapNhap: z.string().optional(),
		tenTinh: z.string().optional(),
		yearsAchieveTop149: z.array(z.number()).nullable().optional()
	}).strip(),
	oldCodes: z.array(z.object({
		maTruong: z.string(),
		maTinh: z.string(),
		maNoiBo: z.string().nullable(),
		tenTruong: z.string()
	}).strip())
}).strip();

/**
 * Build URLSearchParams from SchoolQueryParams (shared between getSchools and getSchoolStats)
 */
function buildSchoolSearchParams(params?: z.infer<typeof SchoolQueryParamsSchema>): URLSearchParams | undefined {
	if (!params) return undefined;

	const searchParams = new URLSearchParams();

	if (params.query) searchParams.append('query', params.query);
	if (params.page) searchParams.append('page', params.page.toString());
	if (params.limit) searchParams.append('limit', params.limit.toString());
	if (params.sortBy) searchParams.append('sortBy', params.sortBy);
	if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
	if (params.top149 !== undefined) searchParams.append('top149', params.top149.toString());
	if (params.hasCodeNameChange !== undefined) searchParams.append('hasCodeNameChange', params.hasCodeNameChange.toString());
	if (params.admissionMethods && params.admissionMethods.length > 0) {
		searchParams.append('admissionMethods', params.admissionMethods.join(','));
	}
	if (params.years && params.years.length > 0) {
		params.years.forEach(year => searchParams.append('years', year.toString()));
	}
	if (params.maKhoa && params.maKhoa.length > 0) {
		params.maKhoa.forEach(code => searchParams.append('maKhoa', code));
	}
	if (params.maNganh && params.maNganh.length > 0) {
		params.maNganh.forEach(code => searchParams.append('maNganh', code));
	}
	if (params.maTinh) {
		const maTinhArray = Array.isArray(params.maTinh) ? params.maTinh : [params.maTinh];
		maTinhArray.forEach(code => searchParams.append('maTinh', code));
	}
	if (params.maTinhMoi && params.maTinhMoi.length > 0) {
		params.maTinhMoi.forEach(code => searchParams.append('maTinhMoi', code));
	}

	return searchParams.toString() ? searchParams : undefined;
}

export const schoolApi = {
	// Get all THPT schools with pagination support
	getSchools: async (params?: z.infer<typeof SchoolQueryParamsSchema>) => {
		const url = buildApiUrl('/thpt', buildSchoolSearchParams(params));
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(TruongTHPTSchema.array()).parse(response.data);
	},

	// Get school by ID
	getSchoolById: async (id: string) => {
		const safeId = `${id}`.trim();

		try {
			const response = await apiClient.get(`/thpt/${safeId}`);
			const payload = response.data;

			if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
				throw new Error('Máy chủ không trả dữ liệu cho mã trường (304 hoặc rỗng)');
			}

			return ApiResponseSchema(TruongTHPTSchema).parse(payload);
		} catch (error: unknown) {
			const axiosErr = error as { response?: { status?: number; data?: { message?: string } }; message?: string };
			if (axiosErr?.response?.status === 304) {
				axiosErr.message = axiosErr.response?.data?.message || 'Máy chủ trả 304 (Not Modified), không có dữ liệu trả về';
			}

			throw error;
		}
	},

	// Get school history (old codes/names)
	getSchoolHistory: async (id: string) => {
		const response = await apiClient.get(`/thpt/${id}/history`);
		return ApiResponseSchema(SchoolHistorySchema).parse(response.data);
	},

	// Get school yearly stats with pagination and sorting
	getSchoolStats: async (params?: z.infer<typeof SchoolQueryParamsSchema>) => {
		const url = buildApiUrl('/thpt/stats/admission', buildSchoolSearchParams(params));
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(SchoolStatsResponseSchema).parse(response.data);
	},

	// Get school stats by URLSearchParams (used by factory hooks)
	getSchoolStatsBySearchParams: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/thpt/stats/admission', params);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(SchoolStatsResponseSchema).parse(response.data);
	},

	// Get school overview stats
	getSchoolOverview: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/thpt/stats/overview', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(SchoolOverviewStatsSchema).parse(response.data);
	},

	// Get school chart data
	getSchoolChartData: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/thpt/stats/chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(UniversalChartDataSchema).parse(response.data);
	},

	// Get Nivo line chart data for schools
	getNivoLineChartData: async (params?: URLSearchParams) => {
		const url = buildApiUrl('/thpt/stats/nivo-chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(NivoLineChartDataSchema).parse(response.data);
	},

	// Get school statistics by year with grouped modes
	getSchoolStatsById: async (schoolId: string, params?: { maKhoa?: string[] }) => {
		const searchParams = new URLSearchParams();
		if (params?.maKhoa && params.maKhoa.length > 0) {
			params.maKhoa.forEach(code => searchParams.append('maKhoa', code));
		}

		const url = buildApiUrl(`/thpt/${schoolId}/stats`, searchParams.toString() ? searchParams : undefined);
		const response = await apiClient.get(url);
		return ApiResponseSchema(SchoolStatsByIdGroupedResponseSchema).parse(response.data);
	},

	// CRUD operations for schools
	createSchool: async (data: Partial<z.infer<typeof TruongTHPTSchema>>) => {
		const response = await apiClient.post('/thpt', data);
		return ApiResponseSchema(TruongTHPTSchema).parse(response.data);
	},

	updateSchool: async (id: string, data: Partial<z.infer<typeof TruongTHPTSchema>>) => {
		const response = await apiClient.put(`/thpt/${id}`, data);
		return ApiResponseSchema(TruongTHPTSchema).parse(response.data);
	},

	updateSchoolNote: async (id: string, data: z.infer<typeof SchoolNoteUpdateSchema>) => {
		const response = await apiClient.patch(`/thpt/${id}/note`, data);
		return ApiResponseSchema(TruongTHPTSchema).parse(response.data);
	},

	deleteSchool: async (id: string): Promise<{ message: string }> => {
		const response = await apiClient.delete<{ message: string }>(`/thpt/${id}`);
		return response.data;
	},

	// Export THPT schools to Excel
	exportToExcel: async (params?: URLSearchParams): Promise<void> => {
		return apiClient.exportToExcel('/thpt/export/excel', params);
	},
};